import React, { useState, useEffect } from 'react';
import { getPersonalityTheme } from '../../CardFiles/theme-config';
import { Brain, Heart, Zap, Shield, Compass, Palette, Trophy, BookOpen, Leaf, Sun, Scale, Star, Eye, Users, Anchor, Sparkles, UserPlus, Send, Copy, Check, X } from 'lucide-react';
import { SharePanel } from './SyncTab';

const renderLucideFromEmoji = (emoji, size = 24) => {
    const props = { size, strokeWidth: 1.5 };
    switch (emoji) {
        case '🧠': return <Brain {...props} />;
        case '❤️': return <Heart {...props} />;
        case '💡': return <Zap {...props} />;
        case '🛡️': return <Shield {...props} />;
        case '🧭': return <Compass {...props} />;
        case '🎨': return <Palette {...props} />;
        case '🏆': return <Trophy {...props} />;
        case '🦉': return <BookOpen {...props} />;
        case '⚡': return <Zap {...props} />;
        case '🌱': return <Leaf {...props} />;
        case '☀️': return <Sun {...props} />;
        case '⚖️': return <Scale {...props} />;
        case '🔮': return <Eye {...props} />;
        case '🤝': return <Users {...props} />;
        case '⚓': return <Anchor {...props} />;
        case '✨': return <Sparkles {...props} />;
        default: return <Star {...props} />;
    }
};

const ProfileTab = ({ user, analysisResult, hasCompletedCard, onViewCards, onResumeTest, onRestart }) => {
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviteLink, setInviteLink] = useState(null);
    const [inviteLoading, setInviteLoading] = useState(false);

    const generateOrFetchInvite = async () => {
        if (inviteLink || inviteLoading) return;
        setInviteLoading(true);
        try {
            // Reuse existing pending invite if available
            if (user?.id) {
                const activeRes = await fetch(`/api/invites/active/${user.id}`);
                const activeData = await activeRes.json();
                if (activeData.success && activeData.invite_link) {
                    setInviteLink(activeData.invite_link);
                    setInviteLoading(false);
                    return;
                }
            }
            // Create a fresh one
            const res = await fetch('/api/invites/create', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    inviter_user_id: user?.id,
                    inviter_test_results: analysisResult || {}
                })
            });
            const data = await res.json();
            if (data.success) setInviteLink(data.invite_link);
        } catch {}
        finally { setInviteLoading(false); }
    };

    useEffect(() => {
        if (user?.id) {
            generateOrFetchInvite();
        }
    }, [user?.id]);

    const displayName = user?.fullName || user?.name || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'User';
    
    // Try to safely extract from generated cards format first, fallback to basic analysis
    const pCard = analysisResult?.cards_data?.personality_card || analysisResult?.personality_card || {};
    const cCard = analysisResult?.cards_data?.character_card || analysisResult?.character_card || {};
    const baseSource = analysisResult || {};

    const leftPercent = pCard?.brainBalance?.leftPercent || baseSource?.left_percent || baseSource?.left_percentage || 50;
    const rightPercent = pCard?.brainBalance?.rightPercent || baseSource?.right_percent || baseSource?.right_percentage || 50;

    // Theme details
    const derivedPersona = leftPercent > 55 ? "The Strategist" : (rightPercent > 55 ? "The Visionary" : "The Catalyst");
    const derivedQuote = leftPercent > 55 ? '"The analytical mind searching for hidden precision."' : (rightPercent > 55 ? '"The empathetic soul with a creator\'s drive."' : '"The balanced orchestrator of logic and intuition."');

    const persona = pCard?.persona || baseSource?.persona || derivedPersona;
    const brainType = typeof pCard?.brainType === 'string' ? pCard.brainType : null;
    const rawQuote = pCard?.oneLiner || derivedQuote;
    const quote = typeof rawQuote === 'string' ? rawQuote : (rawQuote?.text || JSON.stringify(rawQuote));

    // Tags (Traits) -> Map from keyInsights
    const topTraits = Array.isArray(pCard?.keyInsights) && pCard.keyInsights.length > 0
        ? pCard.keyInsights.map(t => typeof t === 'object' ? (t.insight || t.label || t.name || JSON.stringify(t)) : String(t))
        : (baseSource?.insights?.top_traits || baseSource?.trait_analysis?.top_traits || ['Balanced Ambivert', 'Intuitive']);

    // Strengths -> Map from LLM schema objects ({icon, label, description})
    const getStrengthsLabels = () => {
        if (Array.isArray(pCard?.coreStrengths) && pCard.coreStrengths.length > 0) {
            return pCard.coreStrengths.map(s => typeof s === 'object' ? (s.label || s.strength || s.name || JSON.stringify(s)) : String(s));
        }
        return baseSource?.insights?.core_strengths || ['Empathetic Connection', 'Steady Resolve', 'Optimistic Vision'];
    };
    const coreStrengths = getStrengthsLabels();

    // Character traits
    const characterTraits = Array.isArray(cCard?.characterTraits) && cCard.characterTraits.length > 0
        ? cCard.characterTraits.map(t => typeof t === 'object' ? (t.trait || t.name || t.label || JSON.stringify(t)) : String(t))
        : ['Conscientious', 'Perceptive', 'Internal Conflict', 'Focus'];

    return (
        <div className="glass-panel profile-glass">
            <h3 className="section-title">Your Profile</h3>

            {!hasCompletedCard && (
                <div style={{ background: 'rgba(236, 72, 153, 0.1)', border: '1px solid #ec4899', borderRadius: '12px', padding: '16px', marginBottom: '24px', textAlign: 'center' }}>
                    <h4 style={{ color: '#ec4899', margin: '0 0 8px', fontSize: '14px' }}>Profile Incomplete!</h4>
                    <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', margin: '0 0 12px' }}>Upload handwriting to generate your unique character cards and unlock full insights.</p>
                    <button onClick={onResumeTest} style={{ background: '#ec4899', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>Resume Profile Setup ➡️</button>
                </div>
            )}

            <div className="profile-title-row">
                <div className="heart-icon-large">
                    {renderLucideFromEmoji(getPersonalityTheme(persona).icon || '❤️', 28)}
                </div>
                <div>
                    <h2 className="user-name" style={{ fontSize: '22px' }}>{displayName}</h2>
                    <span className="user-class-level" style={{ fontSize: '12px', textTransform: 'uppercase', color: getPersonalityTheme(persona).accent || '#ec4899' }}>{brainType || getPersonalityTheme(persona).name || persona}</span>
                </div>
            </div>

            <p className="profile-quote">"{quote.replace(/^"|"$/g, '')}"</p>

            <div className="dual-bar-container">
                <div className="dual-bar-labels">
                    <span className="left-label">Logic</span>
                    <span className="right-label">Creativity</span>
                </div>
                <div className="dual-bar-percentages">
                    <span style={{ color: '#3b82f6' }}>{leftPercent}%</span>
                    <span style={{ color: '#ec4899' }}>{rightPercent}%</span>
                </div>
                <div className="dual-bar-track">
                    <div className="bar-left-fill" style={{ width: `${leftPercent}%` }}></div>
                    <div className="bar-right-fill" style={{ width: `${rightPercent}%` }}></div>
                </div>
            </div>

            <h4 style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', marginBottom: '12px' }}>Key Personality Tags</h4>
            <div className="tags-container">
                {topTraits.map((trait, index) => (
                    <div className="tag-pill" key={index}>
                        <span className="tag-icon">
                            {index % 2 === 0 ? <Brain size={14} /> : <Scale size={14} />}
                        </span>
                        {trait}
                    </div>
                ))}
            </div>

            <h4 style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', marginBottom: '12px' }}>Top Strengths</h4>
            <div className="strengths-list">
                {coreStrengths.map((str, index) => (
                    <div className="strength-item" key={index}>
                        <div className="strength-name">
                            <span className="strength-icon-wrapper text-pink-400">
                                {str.icon ? renderLucideFromEmoji(str.icon, 16) : renderLucideFromEmoji(['🤝', '⚓', '✨'][index % 3], 16)}
                            </span>
                            {str.label || str}
                        </div>
                        <span className={`strength-badge ${index === 0 ? 'badge-exceptional' : 'badge-strong'}`}>
                            {index === 0 ? 'Exceptional' : 'Strong'}
                        </span>
                    </div>
                ))}
            </div>

            <h4 style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', marginBottom: '12px' }}>Character Traits</h4>
            <div className="traits-grid">
                {characterTraits.map((trait, idx) => (
                    <div className="trait-cell" key={idx}>
                        <span>{trait.icon || ['⭐', '💡', '🌪', '💪'][idx % 4]}</span> {trait.label || trait}
                    </div>
                ))}
            </div>

            {hasCompletedCard ? (
                <div style={{ marginTop: '24px', marginBottom: '16px' }}>
                    <div 
                        onClick={onViewCards}
                        style={{ 
                            position: 'relative',
                            overflow: 'hidden',
                            borderRadius: '20px', 
                            padding: '20px 24px', 
                            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(59, 130, 246, 0.15))', 
                            border: '1px solid rgba(139, 92, 246, 0.3)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.2), inset 0 0 20px rgba(139, 92, 246, 0.1)',
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                        }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 12px 40px rgba(139, 92, 246, 0.2), inset 0 0 20px rgba(139, 92, 246, 0.1)';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.2), inset 0 0 20px rgba(139, 92, 246, 0.1)';
                        }}
                    >
                        {/* Background decoration */}
                        <div style={{ position: 'absolute', right: '-10%', top: '-50%', width: '150px', height: '150px', background: 'radial-gradient(circle, rgba(139, 92, 246, 0.4) 0%, transparent 70%)', filter: 'blur(20px)', pointerEvents: 'none' }}></div>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', zIndex: 1 }}>
                            <h4 style={{ margin: 0, color: 'white', fontSize: '16px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Sparkles size={16} color="#d8b4fe" /> Your Full Analysis
                            </h4>
                            <p style={{ margin: 0, color: 'rgba(255,255,255,0.6)', fontSize: '12px', maxWidth: '200px', lineHeight: '1.4' }}>Strengths, careers, growth tips & more</p>
                        </div>

                        <div style={{ zIndex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {/* Little overlapping cards representation */}
                            <div style={{ display: 'flex', marginRight: '4px' }}>
                                <div style={{ width: '24px', height: '36px', background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)', borderRadius: '4px', transform: 'rotate(-10deg)', boxShadow: '-2px 2px 10px rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.2)' }}></div>
                                <div style={{ width: '24px', height: '36px', background: 'linear-gradient(135deg, #ec4899, #f43f5e)', borderRadius: '4px', transform: 'rotate(10deg) translateX(-12px)', boxShadow: '2px 2px 10px rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.2)' }}></div>
                            </div>
                            <div style={{ background: 'rgba(255,255,255,0.1)', padding: '6px', borderRadius: '50%', display: 'flex' }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
                            </div>
                        </div>
                    </div>

                    {process.env.REACT_APP_ALLOW_USER_RETEST === 'true' && (
                        <div style={{ marginTop: '16px', textAlign: 'center' }}>
                            <button style={{ background: 'transparent', color: 'rgba(255,255,255,0.4)', border: 'none', fontSize: '12px', textDecoration: 'underline', cursor: 'pointer', transition: 'color 0.2s' }} onMouseOver={e => e.currentTarget.style.color = 'white'} onMouseOut={e => e.currentTarget.style.color = 'rgba(255,255,255,0.4)'} onClick={onRestart}>
                                Retake Analysis
                            </button>
                        </div>
                    )}
                </div>
            ) : (
                process.env.REACT_APP_ALLOW_USER_RETEST === 'true' && (
                    <button className="replay-button" onClick={onRestart}>
                        Redo Your Analysis
                    </button>
                )
            )}

            {/* ── Invite & Share Section ── */}
            <div className="invite-cta-section" style={{ marginTop: '20px' }}>
                <div className="invite-cta-header">
                    <span className="invite-cta-emoji">🔮</span>
                    <h3 className="invite-cta-title">See How You Match With Anyone</h3>
                    <p className="invite-cta-subtitle">
                        Send your link. When they take the test, you'll unlock compatibility insights instantly.
                    </p>
                </div>

                <div className="invite-cta-preview-grid">
                    <div className="invite-cta-preview-item">
                        <span>💯</span><span>Compatibility Score</span>
                    </div>
                    <div className="invite-cta-preview-item">
                        <span>🤝</span><span>How You Click</span>
                    </div>
                    <div className="invite-cta-preview-item">
                        <span>⚡</span><span>Friction Points</span>
                    </div>
                    <div className="invite-cta-preview-item">
                        <span>💡</span><span>Tips to Get Along</span>
                    </div>
                </div>

                {!inviteLink ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0', color: 'rgba(255,255,255,0.3)', fontSize: '12px' }}>
                        {inviteLoading ? 'Generating your unique link...' : 'Loading...'}
                    </div>
                ) : (
                    <SharePanel link={inviteLink} />
                )}
            </div>
        </div>
    );
};

export default ProfileTab;
